import React, { useMemo, useState } from 'react';
import { Box, Button, MenuItem, Stack, TextField } from '@mui/material';
import { PamGrid } from '@1823-partners/core';
import StatusCards from './StatusCards';
import StatusChip from './StatusChip';
import TradeDetailDialog from './TradeDetailDialog';
import { STATUS, STATUS_ORDER, TOLERANCE_QTY, TOLERANCE_VAL } from '../../utils/derivativeReconConstants';
import { applyDateFilter, fmt, fmtN } from '../../utils/derivativeReconFormat';
import { wireIdsFor } from '../../utils/derivativeReconLogic';

const numColor = (v, tol) => {
  if (v == null) return undefined;
  return Math.abs(v) > tol ? '#f87171' : '#34d399';
};

function buildPosColumns(expanded) {
  const base = [
    { field: 'id', headerName: 'Trade ID', width: 130, pinned: 'left' },
    { field: 'entity', headerName: 'Entity', width: 90 },
    { field: 'cpName', headerName: 'Broker', width: 140 },
  ];
  const expandedNotional = expanded
    ? [
        { field: 'milVal', headerName: 'Mil Notional', width: 130, type: 'numericColumn', valueFormatter: (p) => fmt(p.value) },
        { field: 'cwVal', headerName: 'CW Notional', width: 130, type: 'numericColumn', valueFormatter: (p) => fmt(p.value) },
      ]
    : [];
  const expandedQty = expanded
    ? [
        { field: 'milQty', headerName: 'Mil # Options', width: 130, type: 'numericColumn', valueFormatter: (p) => fmtN(p.value) },
        { field: 'cwQty', headerName: 'CW Units', width: 110, type: 'numericColumn', valueFormatter: (p) => fmtN(p.value) },
      ]
    : [];
  const expandedStrike = expanded
    ? [
        { field: 'milStrike', headerName: 'Mil Strike', width: 110, type: 'numericColumn', valueFormatter: (p) => fmt(p.value) },
        { field: 'cwStrike', headerName: 'CW Strike', width: 110, type: 'numericColumn', valueFormatter: (p) => fmt(p.value) },
      ]
    : [];
  return [
    ...base,
    ...expandedNotional,
    { field: 'valDiff', headerName: 'Notional Diff', width: 120, type: 'numericColumn', valueFormatter: (p) => fmt(p.value), cellStyle: (p) => ({ color: numColor(p.value, TOLERANCE_VAL), fontWeight: 700 }) },
    ...expandedQty,
    { field: 'qtyDiff', headerName: 'Qty Diff', width: 100, type: 'numericColumn', valueFormatter: (p) => fmtN(p.value), cellStyle: (p) => { if (p.value == null) return {}; return { color: Math.abs(p.value) < TOLERANCE_QTY ? '#34d399' : '#f87171', fontWeight: 700 }; } },
    ...expandedStrike,
    { field: 'strikeDiff', headerName: 'Strike Diff', width: 110, type: 'numericColumn', valueFormatter: (p) => fmt(p.value), cellStyle: (p) => ({ color: numColor(p.value, TOLERANCE_VAL), fontWeight: 700 }) },
    { field: 'cwTrade', headerName: 'CW Trade Dt', width: 120, cellStyle: (p) => { const m = p.data?.tradeDtMatch; if (m === false) return { color: '#f87171', fontWeight: 600 }; if (m === true) return { color: '#34d399', fontWeight: 600 }; return {}; } },
    { field: 'cwSettle', headerName: 'CW Settle Dt', width: 120, cellStyle: (p) => { const m = p.data?.settleDtMatch; if (m === false) return { color: '#f87171', fontWeight: 600 }; if (m === true) return { color: '#34d399', fontWeight: 600 }; return {}; } },
    { field: 'status', headerName: 'Status', width: 170, cellRenderer: (p) => <StatusChip status={p.value} /> },
  ];
}

function buildTxColumns(type, expanded) {
  const base = [
    { field: 'id', headerName: 'Trade ID', width: 140, pinned: 'left' },
    { field: 'entity', headerName: 'Entity', width: 90 },
    { field: 'cpName', headerName: 'Broker', width: 160 },
    { field: 'settle', headerName: type === 'Payoffs' ? 'Payoff Dt' : 'Settle Dt', width: 120 },
  ];
  const expandedTotals = expanded
    ? [
        { field: 'milTotal', headerName: `Mil ${type} Total`, width: 150, type: 'numericColumn', valueFormatter: (p) => fmt(p.value) },
        { field: 'cwTotal', headerName: `CW ${type} Total`, width: 150, type: 'numericColumn', valueFormatter: (p) => fmt(p.value) },
      ]
    : [];
  return [
    ...base,
    ...expandedTotals,
    { field: 'diff', headerName: 'Diff ($)', width: 130, type: 'numericColumn', valueFormatter: (p) => fmt(p.value), cellStyle: (p) => ({ color: numColor(p.value, TOLERANCE_VAL), fontWeight: 700 }) },
    { field: 'wireAmt', headerName: 'Wire Net (custody)', width: 150, type: 'numericColumn', valueFormatter: (p) => (p.value == null ? '' : fmt(Math.abs(p.value))) },
    { field: 'wireDate', headerName: 'Wire Date', width: 120 },
    { field: 'wireAcct', headerName: 'Wire Account', width: 130 },
    { field: 'status', headerName: 'Status', width: 170, cellRenderer: (p) => <StatusChip status={p.value} /> },
  ];
}

export default function ReconTable({
  results,
  type,
  dateFrom,
  dateTo,
  wires = {},
  tradeWireIndex = {},
  cashResults = [],
}) {
  const isPos = type === 'Positions';
  const [filterStatus, setFilterStatus] = useState(null);
  const [filterCp, setFilterCp] = useState('All');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [selected, setSelected] = useState(null);

  const dateField = isPos ? 'milTrade' : 'settle';

  const cpNames = useMemo(
    () => ['All', ...Array.from(new Set(results.map((r) => r.cpName))).sort()],
    [results],
  );

  const dateFiltered = useMemo(
    () => applyDateFilter(results, dateField, dateFrom, dateTo),
    [results, dateField, dateFrom, dateTo],
  );

  // Resolve the custody wire a premium/payoff trade settled on, strictly within
  // the flow type being viewed: the index key, then a scan of the cash results
  // for trades the index missed. A trade normally has both a premium and a
  // payoff, so reading the other type's wire would show a settled amount
  // against a flow that never settled.
  const wiresFor = useMemo(() => {
    if (isPos) return () => [];
    const flowType = type === 'Premiums' ? 'Premium' : 'Payoff';
    const cashByTrade = new Map();
    for (const r of cashResults || []) {
      if (!r || !r.wireId || !r.mil || r.mil.type !== flowType) continue;
      for (const tid of r.mil.tradeIds || []) {
        const k = String(tid).trim();
        if (!k) continue;
        if (!cashByTrade.has(k)) cashByTrade.set(k, []);
        if (!cashByTrade.get(k).includes(r.wireId)) cashByTrade.get(k).push(r.wireId);
      }
    }
    return (id) => {
      const tid = String(id || '').trim();
      if (!tid) return [];
      const ids = wireIdsFor(tradeWireIndex, flowType, tid);
      return (ids.length ? ids : cashByTrade.get(tid) || [])
        .map((wid) => wires[wid])
        .filter(Boolean);
    };
  }, [isPos, type, wires, tradeWireIndex, cashResults]);

  const filtered = useMemo(() => {
    let r = dateFiltered;
    if (filterStatus) r = r.filter((x) => x.status === filterStatus);
    if (filterCp !== 'All') r = r.filter((x) => x.cpName === filterCp);
    if (search) {
      const s = search.toLowerCase();
      r = r.filter(
        (x) =>
          (x.id || '').toLowerCase().includes(s) ||
          (x.cpName || '').toLowerCase().includes(s),
      );
    }
    if (!isPos) {
      r = r.map((row) => {
        // A trade can settle across several wires. The net is their sum; the
        // date and account list each one rather than naming a single wire.
        const ws = wiresFor(row.id);
        return {
          ...row,
          wireAmt: ws.length ? ws.reduce((acc, w) => acc + (w.amt || 0), 0) : null,
          wireDate: ws.map((w) => w.dateStr || '').filter(Boolean).join(' / '),
          wireAcct: ws.map((w) => w.acctNum || '').filter(Boolean).join(' / '),
        };
      });
    }
    return r;
  }, [dateFiltered, filterStatus, filterCp, search, isPos, wiresFor]);

  const counts = useMemo(() => {
    const c = {};
    Object.keys(STATUS).forEach((s) => { c[s] = 0; });
    dateFiltered.forEach((r) => { c[r.status]++; });
    return c;
  }, [dateFiltered]);

  const columnDefs = useMemo(
    () => (isPos ? buildPosColumns(expanded) : buildTxColumns(type, expanded)),
    [isPos, type, expanded],
  );

  return (
    <Box>
      <StatusCards
        statuses={STATUS}
        statusOrder={STATUS_ORDER}
        counts={counts}
        total={dateFiltered.length}
        active={filterStatus}
        onClick={(s) => setFilterStatus(filterStatus === s ? null : s)}
      />
      <Stack direction="row" spacing={1} mb={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <TextField
          size="small"
          placeholder="Search trade ID or broker…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, minWidth: 200 }}
        />
        <TextField
          size="small"
          select
          value={filterCp}
          onChange={(e) => setFilterCp(e.target.value)}
          sx={{ minWidth: 180 }}
        >
          {cpNames.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
        </TextField>
        <Button size="small" variant="outlined" onClick={() => setExpanded((v) => !v)}>
          {expanded ? '⊖ Collapse figures' : '⊕ Expand figures'}
        </Button>
        {(filterStatus || filterCp !== 'All' || search) && (
          <Button size="small" color="error" variant="outlined" onClick={() => { setFilterStatus(null); setFilterCp('All'); setSearch(''); }}>
            ✕ Clear
          </Button>
        )}
      </Stack>
      <Box sx={{ height: 600, width: '100%' }}>
        <PamGrid
          rowData={filtered}
          columnDefs={columnDefs}
          getRowId={(data) => `${data?.id}-${data?.status}`}
          onRowClicked={(e) => setSelected(e.data)}
          defaultColDef={{ sortable: true, resizable: true, filter: true, wrapHeaderText: true, autoHeaderHeight: true }}
          enableShowToolbar={false}
        />
      </Box>
      <TradeDetailDialog
        trade={selected}
        type={type}
        wires={selected ? wiresFor(selected.id) : []}
        onClose={() => setSelected(null)}
      />
    </Box>
  );
}
